<script setup lang="ts">
import {computed, ref} from 'vue'
import {useI18n} from 'vue-i18n'
import {LocaleType} from '@toolify/server/src/services/UserSettingsService/strategies/LocaleUserSettingStrategy/enum/LocaleType'
import {getLanguageTagFromLocaleType} from '@toolify/server/src/services/UserSettingsService/strategies/LocaleUserSettingStrategy/functions/getLanguageTagFromLocaleType'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {i18n} from '@toolify/client/src/setup/i18n'
import ButtonComponent from '@toolify/client/src/modules/ui/components/ButtonComponent.vue'
import ResponsiveDropdownComponent from '@toolify/client/src/modules/modals/components/ResponsiveDropdownComponent.vue'
import FlagComponent from '@toolify/client/src/modules/internalization/components/FlagComponent.vue'
import LocaleChooseListComponent from '@toolify/client/src/modules/internalization/components/LocaleChooseListComponent.vue'
import {LocaleTypeItems} from '@toolify/client/src/modules/internalization/constants/LocaleTypeItems'
import {useGuestLocaleSwitch} from '@toolify/client/src/modules/internalization/composables/useGuestLocaleSwitch'
import {IconWeightType} from '@toolify/client/src/modules/ui/enum/IconWeightType'
import {StyleVariantType} from '@toolify/client/src/modules/ui/enum/StyleVariantType'
import {ColorVariantType} from '@toolify/client/src/modules/ui/enum/ColorVariantType'

interface Props {
  suggestedLocale: LocaleType
}

const props = defineProps<Props>()

const {t} = useI18n()
const {keepLocale, switchLocale} = useGuestLocaleSwitch()

const selectedLocale = ref<LocaleType>(props.suggestedLocale)
const isSwitching = ref(false)

const selectedLocaleItem = computed(() => {
  return LocaleTypeItems.find(localeItem => localeItem.localeType === selectedLocale.value) || LocaleTypeItems[0]
})

// The visitor may not read the page's language yet, so the banner speaks the one their browser asks for
const tInSuggestedLocale = (key: string) => t(key, {}, {locale: props.suggestedLocale})

const onChooseLocale = (locale: LocaleType, close: () => void) => {
  selectedLocale.value = locale
  close()
}

const onContinue = async () => {
  isSwitching.value = true
  try {
    await switchLocale(selectedLocale.value)
  } finally {
    isSwitching.value = false
  }
}

const onClose = () => {
  keepLocale(i18n.global.locale.value as LocaleType)
}
</script>

<template>
  <div
    class="guest-locale-suggestion-banner"
    role="region"
    :aria-label="tInSuggestedLocale('general.selectLanguage')"
    :lang="getLanguageTagFromLocaleType(props.suggestedLocale)"
  >
    <div class="banner-content">
      <p
        class="message"
        :lang="getLanguageTagFromLocaleType(selectedLocale)"
      >{{t('general.localeSuggestion', {}, {locale: selectedLocale})}}</p>
      <div class="locale-select">
        <ResponsiveDropdownComponent
          :dropdownProps="{
            placement: 'bottom-end',
            offsetMainAxis: 8,
          }"
          :bottomSheetProps="{
            title: tInSuggestedLocale('general.selectLanguage'),
          }"
        >
          <template #default="{isOpen}">
            <ButtonComponent
              :style="StyleVariantType.ClearFilled"
              :color="ColorVariantType.Neutral"
              :hasFocus="isOpen"
              hasArrow
            >
              <span class="locale-option">
                <FlagComponent :alphaCode="selectedLocaleItem.flagAlphaCode" :width="16"/>
                {{selectedLocaleItem.title}}
              </span>
            </ButtonComponent>
          </template>
          <template #dropdown="{close}">
            <LocaleChooseListComponent
              :selectedLocale="selectedLocale"
              @choose="locale => onChooseLocale(locale, close)"
            />
          </template>
        </ResponsiveDropdownComponent>
      </div>
      <ButtonComponent
        class="continue-button"
        :isLoading="isSwitching"
        :lang="getLanguageTagFromLocaleType(selectedLocale)"
        @click="onContinue"
      >
        {{t('workspaces.continue', {}, {locale: selectedLocale})}}
      </ButtonComponent>
      <ButtonComponent
        class="close-button"
        :iconNameType="IconNameType.Xmark"
        :iconWeightType="IconWeightType.Solid"
        :style="StyleVariantType.ClearClear"
        :color="ColorVariantType.Neutral"
        round
        :aria-label="tInSuggestedLocale('general.close')"
        :title="tInSuggestedLocale('general.close')"
        @click="onClose"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@toolify/client/src/styles/variables' as variables;

.guest-locale-suggestion-banner {
  display: flex;
  justify-content: center;
  box-sizing: border-box;
  padding: map-get(variables.$spacing, space-3) map-get(variables.$spacing, space-4);
  background: map-get(variables.$colors, container-low);
  color: map-get(variables.$colors, content-normal);

  @media (max-width: variables.$breakpointLargeMax) {
    padding-top: calc(#{variables.$safeAreaTop} + #{map-get(variables.$spacing, space-2)});
    padding-left: calc(#{variables.$safeAreaLeft} + #{map-get(variables.$spacing, space-4)});
    padding-right: calc(#{variables.$safeAreaRight} + #{map-get(variables.$spacing, space-2)});
  }
}

.banner-content {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto auto;
  grid-template-areas: 'message select continue close';
  align-items: center;
  column-gap: map-get(variables.$spacing, space-3);
  row-gap: map-get(variables.$spacing, space-2);
  width: 100%;
  max-width: 64rem;

  @media (max-width: variables.$breakpointLargeMax) {
    grid-template-columns: auto auto minmax(0, 1fr) auto;
    grid-template-areas:
      'message message message close'
      'select continue . .';
    column-gap: map-get(variables.$spacing, space-2);
  }
}

.message {
  grid-area: message;
  margin: 0;
}

.locale-select {
  grid-area: select;
}

.locale-option {
  display: inline-flex;
  align-items: center;
  gap: map-get(variables.$spacing, space-1-5);
}

.continue-button {
  grid-area: continue;
}

.close-button {
  grid-area: close;
}
</style>
